import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import LinkButton from '@/components/ui/link-button'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import dayjs from 'dayjs'
import { LinkIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useEventImportCandidates } from '../hooks/useEventImportCandidates'

type Props = {
  event: wowaudit.Event
  open: boolean
  onOpenChange: (open: boolean) => void
  onImport: (sourceEventId: number) => Promise<void>
}

const ImportFromPreviousEventDialog = ({
  event,
  open,
  onOpenChange,
  onImport,
}: Props) => {
  const { team } = useTeam()
  const [importingEventId, setImportingEventId] = useState<number | null>(null)
  const { importCandidates, isFetching } = useEventImportCandidates(
    team.id,
    event.id,
    open,
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import from previous event</DialogTitle>
        </DialogHeader>
        <div className='max-h-[65vh] mt-2 overflow-y-auto pr-1'>
          <div className='space-y-3 pr-3'>
            {isFetching ? (
              <div className='h-28 flex items-center justify-center text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : importCandidates.length === 0 ? (
              <div className='h-20 flex items-center justify-center text-sm text-muted-foreground'>
                No previous events found
              </div>
            ) : (
              importCandidates.map((candidate) => {
                const isImporting = importingEventId === candidate.event.id
                return (
                  <Card key={candidate.event.id}>
                    <div className='flex items-center justify-between'>
                      <div>
                        <div className='text-sm font-medium'>
                          {dayjs(candidate.event.date).format(
                            'ddd, MMM D YYYY',
                          )}
                        </div>
                        <div className='text-xs text-muted-foreground truncate'>
                          {candidate.event.title || 'Event'}
                        </div>
                      </div>

                      <div className='flex items-center gap-2'>
                        <LinkButton
                          to={`${team.path}/events/${candidate.event.id}`}
                          rel='noreferrer'
                          target='_blank'
                          size='sm'
                        >
                          <LinkIcon size={14} />
                        </LinkButton>
                        <Button
                          type='button'
                          size='sm'
                          disabled={importingEventId !== null}
                          loading={isImporting}
                          onClick={async () => {
                            setImportingEventId(candidate.event.id)
                            try {
                              await onImport(candidate.event.id)
                              onOpenChange(false)
                            } finally {
                              setImportingEventId(null)
                            }
                          }}
                        >
                          Import
                        </Button>
                      </div>
                    </div>

                    <div className='flex items-start gap-1 mt-3 flex-wrap'>
                      {candidate.activities.map((activity) => (
                        <div
                          key={activity.id}
                          className='w-10 flex flex-col items-center gap-1'
                        >
                          {activity.thumbnailUrl ? (
                            <img
                              src={activity.thumbnailUrl}
                              className='w-8 h-8 rounded border border-border'
                            />
                          ) : (
                            <div className='w-8 h-8 rounded border border-border bg-muted' />
                          )}
                          <span className='text-xs font-mono text-muted-foreground leading-none'>
                            {activity.selectedCount}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImportFromPreviousEventDialog
